/**
 * Caret / selection helpers for MathEditor's iPad "tap landed on a select-all"
 * fallback. Kept out of `MathEditor.tsx` so the decision logic is pure and unit
 * testable — jsdom cannot render MathLive, but every predicate here works on
 * plain numbers, rects and LaTeX strings.
 *
 * Background: on iPad, MathLive sometimes cannot resolve a tap to a precise atom
 * (typically an empty placeholder such as a fraction's denominator) and falls
 * back to selecting the entire expression. MathEditor detects that state and
 * collapses it to a caret at the tapped position.
 */
/** Max pointer travel (px) still counted as a tap rather than a drag-select. */
export var TAP_SLOP_PX = 8;
/**
 * True when `el` IS a `<math-field>` or lives inside one.
 *
 * Must be a `closest()` walk, not a tag test on the node itself: a touch inside
 * a math-field lands on one of MathLive's inner spans (`.ML__cmr`, the ▢
 * placeholder box), and — when the event crosses the shadow boundary — on the
 * host itself. Both must count as "inside".
 */
export var isInsideMathField = function (el) { var _a; return !!((_a = el === null || el === void 0 ? void 0 : el.closest) === null || _a === void 0 ? void 0 : _a.call(el, 'math-field')); };
/**
 * Whether MathEditor's long-press word-select fallback may be ARMED for this
 * `touchstart`.
 *
 * The fallback exists because iPad Safari sometimes never fires a native
 * selection for a long press on the plain contenteditable. It must NEVER run
 * over a `<math-field>`: MathLive owns the caret/selection there and the
 * fallback has no notion of atoms — a math-field is opaque to
 * `caretRangeFromPoint`, so the range resolves to the editorDiv and
 * `sel.modify('extend', 'forward', 'word')` expands over the WHOLE element. On
 * iPad, pressing a fraction's denominator for >500ms highlighted the entire
 * editor box, and the resulting focus move out of the math-field closed the
 * math keypad mid-edit.
 *
 * The math-field stops `pointerdown`/`mousedown`/`click` propagation but NOT
 * `touchstart`, so this guard is the only thing keeping the fallback off the
 * math context.
 */
export var shouldArmLongPressWordSelect = function (ctx) {
    return ctx.isFocused &&
        !ctx.viewOnly &&
        ctx.isTouchDevice &&
        !isInsideMathField(ctx.target);
};
/**
 * Whether the point (x, y) currently sits over a `<math-field>` — the SECOND
 * guard, re-evaluated when the 500ms long-press timer fires.
 *
 * Re-checks the point itself rather than focus, because layout can shift
 * between `touchstart` and the timer (a keypad insert widens the box), so the
 * press may now be over a math-field even though the touchstart target wasn't.
 * Deliberately NOT `document.activeElement === MATH-FIELD`: in a mixed
 * text + math editor a math-field legitimately holds focus while the keypad is
 * open, and long-pressing plain text in that state must still select the word.
 */
export var isPointOverMathField = function (x, y, doc) {
    var _a;
    if (doc === void 0) { doc = typeof document !== 'undefined' ? document : undefined; }
    return isInsideMathField((_a = doc === null || doc === void 0 ? void 0 : doc.elementFromPoint) === null || _a === void 0 ? void 0 : _a.call(doc, x, y));
};
/**
 * True when the LaTeX still holds at least one placeholder box.
 *
 * Must NOT be an `includes('\\placeholder{}')` test: the keypad's fraction
 * button inserts `\frac{\placeholder{ }}{\placeholder{ }}` (a SPACE as the
 * placeholder's default value) and MathLive round-trips that space through
 * `mf.value`. The exact-string test therefore missed every freshly
 * keypad-inserted fraction — which is why the iPad select-all fallbacks never
 * fired when a denominator placeholder was tapped.
 */
export var hasPlaceholder = function (latex) {
    return !!latex && /\\placeholder(\{[^}]*\})?/.test(latex);
};
/**
 * True when the pointer barely moved between down and up — i.e. a tap, not a
 * drag. A drag that happens to cover the whole expression is a deliberate
 * select-all the user is about to act on (delete, recolour, bold), so it must
 * never be collapsed.
 *
 * A missing `down` counts as a DRAG, not a tap: a real tap on the field always
 * fires `pointerdown` on that field first, so the only way `down` is absent is
 * a gesture that began somewhere else — e.g. a drag-select started in the
 * surrounding text and released inside the math-field. Collapsing that would
 * throw away the user's selection.
 */
export var isTapWithinSlop = function (down, upX, upY) { return !!down && Math.hypot(upX - down.x, upY - down.y) <= TAP_SLOP_PX; };
/**
 * True when (x, y) lies inside `rect`. Guards against a synthetic pointerup
 * (iOS replays one at 0,0 after some gestures), which would otherwise resolve
 * to offset 0 and drop the caret at the start of the expression.
 */
export var isPointInsideRect = function (rect, x, y) {
    return typeof x === 'number' &&
        typeof y === 'number' &&
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom;
};
/**
 * True when the math-field's selection spans its entire content — the state
 * MathLive falls back to when a tap can't be resolved to an atom.
 */
export var isMathSelectAll = function (mf) {
    var _a, _b;
    var ranges = ((_a = mf.selection) === null || _a === void 0 ? void 0 : _a.ranges) || [];
    var lastOffset = (_b = mf.lastOffset) !== null && _b !== void 0 ? _b : 0;
    return ranges.length === 1 && ranges[0][0] === 0 && ranges[0][1] === lastOffset && lastOffset > 0;
};
/**
 * Whether the atom the caret sits just after is a placeholder box — i.e. the
 * resolved offset landed on `▢` rather than on real content.
 *
 * MathLive positions are atom-indexed: the caret at offset N sits immediately
 * after the Nth atom, so `model.at(N)` is the atom the user tapped.
 *
 * Returns `undefined` — NOT `false` — when the private `_mathfield.model` can't
 * be read, so a future MathLive version that moves it degrades to the safe
 * placeholder-selecting fallback rather than silently reintroducing the
 * caret-beside-the-box corruption.
 */
var isPlaceholderAtOffset = function (mf, offset) {
    var _a, _b, _c;
    if (offset <= 0)
        return false;
    try {
        var atom = (_c = (_b = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model) === null || _b === void 0 ? void 0 : _b.at) === null || _c === void 0 ? void 0 : _c.call(_b, offset);
        if (!atom || typeof atom.type !== 'string')
            return undefined;
        return atom.type === 'placeholder';
    }
    catch (_d) {
        return undefined;
    }
};
/**
 * Collapse a select-all inside a math-field to a caret. Prefers the offset under
 * (x, y) — the point the user actually tapped — so tapping a fraction's
 * denominator lands in the denominator rather than wherever the next placeholder
 * happens to be. Falls back to the next placeholder (then the end of the
 * expression) when the point is outside the field, when MathLive can't resolve
 * it, or when no coordinates are available (the focus-event path).
 *
 * When the tapped atom IS a placeholder box, the placeholder is *selected*
 * rather than merely stepped past — matching what `moveToNextPlaceholder` does —
 * so the next character replaces the box. Leaving a bare caret after it instead
 * kept the box alive: typing `3` into a tapped denominator produced
 * `\frac{5}{\placeholder{}3}`, which renders a stray ▢ and speaks as
 * "the fraction 5 over placeholder 3".
 */
export var collapseCaretAtPoint = function (mf, x, y) {
    var _a, _b;
    if (isPointInsideRect(mf.getBoundingClientRect(), x, y)) {
        var offset = (_b = (_a = mf).getOffsetFromPoint) === null || _b === void 0 ? void 0 : _b.call(_a, x, y);
        if (typeof offset === 'number' && offset >= 0) {
            var onPlaceholder = isPlaceholderAtOffset(mf, offset);
            if (onPlaceholder) {
                mf.selection = { ranges: [[offset - 1, offset]] };
                return;
            }
            if (onPlaceholder === false) {
                mf.position = offset;
                return;
            }
            // undefined — couldn't inspect the atom. Fall through to the
            // placeholder-selecting fallback below rather than risk a caret that
            // leaves the box alive.
        }
    }
    if (hasPlaceholder(mf.value))
        mf.executeCommand('moveToNextPlaceholder');
    else
        mf.executeCommand('moveToMathfieldEnd');
};
